import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRiskIndexMarker } from './RiskIndexFootnotes';

const VARIANT_CLASSES = {
  badge: 'bg-[#F0F0F0] rounded-[4px] px-2 py-1 text-xs font-bold inline-block',
  bold: 'text-xs font-bold',
  large: 'text-2xl font-bold text-[#105235]',
  inline: '',
};

const formatIndex = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : value;
};

/**
 * Displays IRS or IRE value with superscript + tooltip when needed.
 *
 * - isNd flag (or no value at all) → "ND" + superscript "†" + tooltip
 * - Expert-assigned → value + superscript "††" + tooltip
 * - Normal → plain value
 */
export default function RiskIndexDisplay({ value, isNd = false, isExpert = false, variant = 'badge', className = '', expertTooltip }) {
  const { t } = useTranslation('common');
  const baseClass = `${VARIANT_CLASSES[variant] || ''} ${className}`.trim();

  const isNdCase =
    isNd || value === null || value === undefined || value === '';

  // Declares the marker to the enclosing <RiskIndexFootnotes>, which renders
  // the matching note under the table. No-op outside one.
  useRiskIndexMarker(isNdCase ? 'nd' : isExpert ? 'expert' : null);

  // ND is an explicit backend state (`*_risk_index_is_nd`), never inferred from
  // the value: an index legitimately equal to 0 must render "0", not "ND".
  // A missing value (null/undefined/'') still falls back to ND — nothing to show.
  // Hover lives on the dagger superscript only — the "ND" label itself stays
  // inert. Same pattern for the expert (††) case below.
  if (isNdCase) {
    return (
      <span className={baseClass}>
        {t('risk_index.nd')}
        <Tooltip title={t('risk_index.nd_tooltip')}>
          <sup className="text-[10px] ml-0.5 cursor-help">{'\u2020'}</sup>
        </Tooltip>
      </span>
    );
  }

  const displayValue = formatIndex(value);

  // Expert-assigned case
  if (isExpert) {
    return (
      <span className={baseClass}>
        {displayValue}
        <Tooltip title={expertTooltip || t('risk_index.expert_tooltip')}>
          <sup className="text-[10px] ml-0.5 cursor-help">{'\u2020\u2020'}</sup>
        </Tooltip>
      </span>
    );
  }

  // Normal value
  return <span className={baseClass}>{displayValue}</span>;
}
